/**
 * Brain endpoint handoff for AgentForge.
 *
 * Deep link: ?endpoint=…&kind=openai-vision&model=…
 * Catalog Connect (HF / Cloudflare / CivitAI / Forge endpoints) becomes the brain.
 */

import type { InferenceSource, InferenceTarget } from "../suite/inference";
import { isCatalogSource } from "../suite/inference";

export type AgentBindKind = "openai-vision" | "vlm" | "openai" | "suite" | string;

export type AgentBrain = {
  bindKind: AgentBindKind;
  endpointUrl: string;
  model?: string;
  apiKey?: string;
  label?: string;
  source: InferenceSource;
  instanceId?: string;
};

export type BrainMode = InferenceSource;

const EP_KEY = "agentforge.endpoint.v1";
const MODE_KEY = "agentforge.brain_mode.v1";
const INST_KEY = "agentforge.instance.v1";

export function normalizeEndpointUrl(raw: string): string {
  const u = (raw || "").trim().replace(/\/$/, "");
  if (!u) return "";
  if (!/^https?:\/\//i.test(u)) return `https://${u}`;
  return u;
}

function normalizeSource(raw: string | null | undefined): InferenceSource {
  if (isCatalogSource(raw) || raw === "custom") return raw;
  return "custom";
}

export function loadEndpoint(): AgentBrain | null {
  try {
    const raw = localStorage.getItem(EP_KEY);
    if (!raw) return null;
    const j = JSON.parse(raw) as AgentBrain;
    if (!j?.endpointUrl) return null;
    return { ...j, source: normalizeSource(j.source) };
  } catch {
    return null;
  }
}

export function saveEndpoint(ep: AgentBrain | null): void {
  try {
    if (!ep) localStorage.removeItem(EP_KEY);
    else localStorage.setItem(EP_KEY, JSON.stringify({ ...ep, source: normalizeSource(ep.source) }));
  } catch {
    /* ignore */
  }
}

export function loadBrainMode(): BrainMode {
  try {
    const v = localStorage.getItem(MODE_KEY);
    if (isCatalogSource(v) || v === "custom") return v;
  } catch {
    /* ignore */
  }
  return "cloudflare";
}

export function saveBrainMode(m: BrainMode): void {
  try {
    localStorage.setItem(MODE_KEY, isCatalogSource(m) || m === "custom" ? m : "cloudflare");
  } catch {
    /* ignore */
  }
}

export function loadCachedInstanceId(): string | null {
  try {
    return localStorage.getItem(INST_KEY);
  } catch {
    return null;
  }
}

export function saveCachedInstanceId(id: string | null): void {
  try {
    if (!id) localStorage.removeItem(INST_KEY);
    else localStorage.setItem(INST_KEY, id);
  } catch {
    /* ignore */
  }
}

export function brainToInferenceTarget(brain: AgentBrain | null): InferenceTarget | null {
  if (!brain?.endpointUrl) return null;
  return {
    baseUrl: normalizeEndpointUrl(brain.endpointUrl),
    apiKey: brain.apiKey || "",
    model: brain.model || "default",
    source: normalizeSource(brain.source),
    label: brain.label || brain.endpointUrl,
    instanceId: brain.instanceId,
    bindKind: brain.bindKind,
  };
}

/** Consume ?endpoint=&kind=&model= from an external handoff. Stored as custom. */
export function consumeBinderQuery(): AgentBrain | null {
  try {
    const sp = new URLSearchParams(window.location.search);
    const endpoint = sp.get("endpoint") || sp.get("url");
    const kind = sp.get("kind") || sp.get("bindKind") || "openai-vision";
    const model = sp.get("model") || undefined;
    if (!endpoint) return loadEndpoint();
    const ep: AgentBrain = {
      bindKind: kind,
      endpointUrl: normalizeEndpointUrl(endpoint),
      model,
      source: "custom",
      label: "Handoff endpoint",
    };
    saveEndpoint(ep);
    saveBrainMode("custom");
    const url = new URL(window.location.href);
    for (const k of ["endpoint", "url", "kind", "bindKind", "model"]) {
      url.searchParams.delete(k);
    }
    history.replaceState(null, "", url.pathname + (url.search || "") + url.hash);
    return ep;
  } catch {
    return loadEndpoint();
  }
}

export async function probeEndpointHealth(
  ep: AgentBrain,
): Promise<{ ok: boolean; message: string }> {
  const base = ep.endpointUrl.replace(/\/$/, "");
  const paths = ["/health", "/v1/models", "/"];
  for (const p of paths) {
    try {
      const res = await fetch(`${base}${p === "/" ? "" : p}`, {
        method: "GET",
        signal: AbortSignal.timeout(8000),
        headers: ep.apiKey ? { Authorization: `Bearer ${ep.apiKey}` } : {},
      });
      if (res.ok || res.status === 404) {
        return { ok: res.ok || res.status < 500, message: `HTTP ${res.status} @ ${p}` };
      }
    } catch (e) {
      if (p === paths[paths.length - 1]) {
        return { ok: false, message: e instanceof Error ? e.message : String(e) };
      }
    }
  }
  return { ok: false, message: "No health response" };
}
